import type {
  CultureSelectorCurrency,
  CultureSelectorLocale,
  CultureSelectorMarket,
  CultureCurrencyResponse,
  CultureLocaleResponse,
  CultureMarketResponse,
  Currency,
  Locale,
  Market,
} from '@web-platform/global-components-types/culture';

const pickLocaleFields = ({ Name, NativeName }: Locale) => localeTitleCase({ Name, NativeName });
const pickCurrencyFields = ({ Code, Symbol }: Currency) => ({ Code, Symbol });
const pickMarketFields = ({
  DefaultCurrencyCode,
  DefaultLocaleName,
  Domain,
  IsPreRelease,
  MarketCode,
  SecondaryLocaleNames,
  TranslatedName,
}: Market) => ({
  DefaultCurrencyCode,
  DefaultLocaleName,
  Domain,
  IsPreRelease,
  MarketCode,
  SecondaryLocaleNames,
  TranslatedName,
});

const caseInsensitiveLocaleCompare = (a?: string, b?: string, localeName?: string) => {
  if (typeof a !== 'undefined' && typeof b !== 'undefined') {
    return a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase(), localeName);
  }
  return 0;
};

const capitalize = (str: string) =>
  str.charAt(0).toLocaleUpperCase() + str.slice(1).toLocaleLowerCase();

const titleCase = (str: string) => str.replace(/([^\s()]+)/g, capitalize);

const localeTitleCase = (localeObj: CultureSelectorLocale) =>
  Object.assign(localeObj, {
    NativeName: titleCase(localeObj?.NativeName),
  });

/**
 * Formats the locales for the culture selector. It filters out locales not supported on web and removes pre-release locales if not requested specifically.
 * Sorts the remaining locales in a case-insensitive manner by their native names.
 * @public
 *
 * @param csLocales - A response object containing an array of Locale items from the Culture Service.
 * @param showPreRelease - The value of `serve_pre_release_locales` from the Configuration Service.
 *
 * @returns An array of CultureSelectorLocale objects, sorted by their native names.
 *
 * @example
 * ```ts
 * import { formatLocales } from '@skyscanner-internal/global-component/culture-selector/data';
 *
 * // showPreReleaseLocales = "serve_pre_release_locales" from the Configuration Service
 * // csLocales = array of locales provided by Culture Service
 *
 * const formattedLocales = formatLocales(csLocales, showPreReleaseLocales);
 * ```
 */
export const formatLocales = (
  csLocales: CultureLocaleResponse,
  showPreRelease: boolean,
): CultureSelectorLocale[] =>
  csLocales.Items.filter(
    (locale: Locale) => locale.SupportedOnWeb || (showPreRelease && locale.IsPreRelease),
  )
    .map(pickLocaleFields)
    .sort((a, b) => caseInsensitiveLocaleCompare(a.NativeName, b.NativeName));

/**
 * Formats the currencies for the culture selector. Sorts the currencies in a case-insensitive manner by their code.
 * @public
 *
 * @param csCurrencies - A response object containing an array of Currency items from the Culture Service.
 *
 * @returns An array of CultureSelectorCurrency objects, sorted by their code.
 *
 * @example
 * ```ts
 * import { formatCurrencies } from '@skyscanner-internal/global-component/culture-selector/data';
 *
 * // csCurrencies = array of currencies provided by Culture Service
 *
 * const formattedCurrencies = formatCurrencies(csCurrencies);
 * ```
 */
export const formatCurrencies = (
  csCurrencies: CultureCurrencyResponse,
): CultureSelectorCurrency[] =>
  csCurrencies.Items.filter((c) => c.Code !== 'BGN')
    .map(pickCurrencyFields)
    .sort((a, b) => caseInsensitiveLocaleCompare(a.Code, b.Code));

/**
 * @public
 */
export function formatMarkets(csMarkets: CultureMarketResponse): CultureSelectorMarket[];

/**
 * @public
 */
export function formatMarkets(
  csMarkets: CultureMarketResponse,
  unsupportedMarkets: string[],
): CultureSelectorMarket[];

/**
 * @public
 */
export function formatMarkets(
  csMarkets: CultureMarketResponse,
  options: {
    unsupportedMarkets?: string[];
    localeName?: string;
  },
): CultureSelectorMarket[];

/**
 * Formats the markets for the culture selector. Filters out unsupported and pre-release markets.
 * Sorts the markets in a case-insensitive manner by their translated name.
 * @public
 *
 * @param csMarkets - A response object containing an array of Market items from the Culture Service.
 * @param unsupportedMarketsOrOptions - Either:
 *   - An array of market code strings to exclude (deprecated).
 *   - An options object:
 *       @property unsupportedMarkets - (Optional) Array of market code strings to exclude.
 *       @property localeName - (Optional) Locale string to use for sorting.
 *
 * @returns An array of CultureSelectorMarket objects, sorted by their translated name.
 *
 * @example
 * ```ts
 * import { formatMarkets } from '@skyscanner-internal/global-component/culture-selector/data';
 *
 * // csMarkets = array of markets provided by Culture Service
 * // unsupportedMarkets = list of unsupported markets
 *
 * // without unsupported markets
 * const formattedMarkets = formatMarkets(csMarkets);
 *
 * // with a list of unsupported markets
 * const formattedMarkets = formatMarkets(csMarkets, ['AQ', 'BQ']);
 * ```
 */
export function formatMarkets(
  csMarkets: CultureMarketResponse,
  unsupportedMarketsOrOptions?:
    | string[]
    | {
        unsupportedMarkets?: string[];
        localeName?: string;
      },
): CultureSelectorMarket[] {
  const unsupportedMarketCodes = ['AQ', 'BQ', 'CW', 'TL', 'KO', 'BL', 'SS', 'SX'];

  let unsupportedMarkets: string[];
  let localeName: string | undefined;

  if (Array.isArray(unsupportedMarketsOrOptions)) {
    unsupportedMarkets = unsupportedMarketsOrOptions;
    localeName = undefined;
  } else if (unsupportedMarketsOrOptions && typeof unsupportedMarketsOrOptions === 'object') {
    unsupportedMarkets = unsupportedMarketsOrOptions.unsupportedMarkets ?? unsupportedMarketCodes;
    localeName = unsupportedMarketsOrOptions.localeName;
  } else {
    unsupportedMarkets = unsupportedMarketCodes;
    localeName = undefined;
  }

  return csMarkets.Items.filter(
    (market) => !unsupportedMarkets.includes(market.MarketCode) && !market.IsPreRelease,
  )
    .map(pickMarketFields)
    .sort((a, b) => caseInsensitiveLocaleCompare(a.TranslatedName, b.TranslatedName, localeName));
}
